import type { Dict } from '@aviata/platform-types';
import dayjs from 'dayjs';
import { type flightEntity } from 'freedom-shared';
import { dictEntity, type hotOffers } from 'shared';

import type { hotOffersTypes } from '@/entities/hot-offers';

import { mapOffersResults } from '@/shared/lib';

import { mapWatchlistOfferToMappedFlights } from './map-watchlist-offer-to-flights';
import { parseDirectionFromSearchQuery } from './parse-search-query';
import type { WatchlistOffer, WatchlistOfferRecommendation } from '../types';

const fallbackLocalized = (iata: string): hotOffers.LocalizedNames => ({
  name: iata,
  name_ru: iata,
  name_en: iata,
  name_kk: iata,
});

const buildAirport = (iata: string): hotOffers.Airport => ({
  ...fallbackLocalized(iata),
  code: iata,
});

const buildCityFromDict = (
  iata: string,
  dictCity: Dict.Cities.City | undefined,
): hotOffers.City => {
  if (!dictCity) {
    return { ...fallbackLocalized(iata), code: iata };
  }
  return {
    code: dictCity.iata || iata,
    name: dictCity.name ?? iata,
    name_ru: dictCity.name_ru ?? dictCity.name ?? iata,
    name_en: dictCity.name_en ?? dictCity.name ?? iata,
    name_kk: dictCity.name_kk ?? dictCity.name ?? iata,
  };
};

const buildCountryFromDict = (
  dictCountry: Dict.Cities.City['country'] | undefined,
): hotOffers.Country => {
  if (!dictCountry) {
    return {
      ...fallbackLocalized(''),
      code: '',
      iso_code: '',
    };
  }
  return {
    code: dictCountry.iso_code ?? '',
    iso_code: dictCountry.iso_code ?? '',
    name: dictCountry.name ?? '',
    name_ru: dictCountry.name_ru ?? dictCountry.name ?? '',
    name_en: dictCountry.name_en ?? dictCountry.name ?? '',
    name_kk: dictCountry.name_kk ?? dictCountry.name ?? '',
  };
};

/**
 * The workspace ships two copies of `@aviata/platform-types` (one nested
 * under the app, one at the root) so the `Dict.Entity` we receive here is
 * nominally distinct from the one `dictEntity.lib` was compiled against.
 * We narrow with the lib's own parameter type to silence those false
 * positives without losing inference inside the call.
 */
type DictArg = Parameters<typeof dictEntity.lib.getCity>[1];

const buildLocation = (
  airportIata: string,
  cityIata: string,
  dict: Dict.Entity | undefined,
): hotOffers.Location => {
  const dictCity = dict
    ? dictEntity.lib.getCity(cityIata, dict as unknown as DictArg)
    : undefined;
  return {
    airport: buildAirport(airportIata),
    city: buildCityFromDict(cityIata, dictCity),
    country: buildCountryFromDict(dictCity?.country),
  };
};

const buildAirlines = (
  airlineIatas: string[],
  dict: Dict.Entity | undefined,
): hotOffers.Result['display_airlines'] => {
  return airlineIatas.map(code => {
    const dictAirline = dict
      ? dictEntity.lib.getAirline(code, dict as unknown as DictArg)
      : undefined;
    return {
      code,
      name: dictAirline?.name ?? code,
      name_ru: dictAirline?.name_ru ?? dictAirline?.name ?? code,
      name_en: dictAirline?.name_en ?? dictAirline?.name ?? code,
      name_kk: dictAirline?.name_kk ?? dictAirline?.name ?? code,
    };
  });
};

/**
 * Resolves the country ISO code for an airport via the dict. Returns `''`
 * when the airport (or its city / country) is missing, so callers can
 * filter falsy values out before rendering flags.
 */
const getCountryCodeByAirport = (
  airportIata: string,
  dict: Dict.Entity | undefined,
): string => {
  if (!dict || !airportIata) {
    return '';
  }
  const dictCity = dictEntity.lib.getCityByAirport(
    airportIata,
    dict as unknown as DictArg,
  );
  return dictCity?.country?.iso_code ?? '';
};

/**
 * For each flight in the offer, returns the unique country ISO codes the
 * flight passes through, in route order (origin → layovers → destination).
 * Used to render the country-flag strip on the recommended-offer card.
 */
const buildFlightCountryCodes = (
  watchlistOffer: WatchlistOffer,
  dict: Dict.Entity | undefined,
): string[][] => {
  return watchlistOffer.flights.map(flight => {
    const seen = new Set<string>();
    const codes: string[] = [];
    for (const segment of flight.segments) {
      for (const airport of [segment.dep.airport, segment.arr.airport]) {
        const code = getCountryCodeByAirport(airport, dict);
        if (code && !seen.has(code)) {
          seen.add(code);
          codes.push(code);
        }
      }
    }
    return codes;
  });
};

export type MappedRecommendedOffer = hotOffersTypes.MappedHotOffer & {
  /**
   * Hash of the concrete offer the backend returned for this saved search,
   * so the SERP can deep-link straight to its details page.
   */
  offerHash: string;
  /**
   * All flights of the offer (outbound + any return / multi-city legs) mapped
   * to the shape `SearchCardFlight` consumes, so the card can render every
   * direction instead of just the first segment.
   */
  mappedFlights: flightEntity.types.MappedFlight[];
  /**
   * Country ISO codes per flight, 1:1 with `mappedFlights` and deduplicated
   * in route order. Used to render the country-flag strip on each flight row.
   */
  flightCountryCodes: string[][];
};

/**
 * Adapts a `/watchlist/offers` response item to `MappedHotOffer` so the
 * existing offers carousel card can render it without changes. The original
 * offer hash and all flights are preserved so callers can navigate to that
 * specific offer's details page and render every leg of the trip.
 */
export function mapWatchlistOfferRecommendation(
  item: WatchlistOfferRecommendation,
  dict: Dict.Entity | undefined,
): MappedRecommendedOffer {
  const flight = item.offer.flights[0];
  const segments = flight?.segments ?? [];
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  const originAirport = firstSegment?.dep.airport ?? '';
  const destinationAirport = lastSegment?.arr.airport ?? '';
  /**
   * `search_query` carries CITY codes (e.g. `ALA-MOW`) while flight segments
   * carry AIRPORT codes (e.g. `ALA-VKO`). For the direction line we want the
   * city, so prefer the parsed query and fall back to the airport iata only
   * when the query is unparseable.
   */
  const direction = parseDirectionFromSearchQuery(item.search_query);
  const originCityIata = direction?.dep ?? originAirport;
  const destinationCityIata = direction?.arr ?? destinationAirport;
  const depAt = firstSegment?.dep.at ?? '';
  const arrAt = lastSegment?.arr.at ?? '';
  const flightTime =
    depAt && arrAt ? Math.max(0, dayjs(arrAt).diff(dayjs(depAt), 'second')) : 0;

  const airlineCodes = Array.from(
    new Set(segments.map(segment => segment.airline)),
  );

  const result: hotOffers.Result = {
    origin: buildLocation(originAirport, originCityIata, dict),
    destination: buildLocation(destinationAirport, destinationCityIata, dict),
    price: { currency: item.currency, amount: item.price },
    dep_date: depAt ? (depAt.split('T')[0] ?? '') : '',
    search_query: item.search_query,
    created_at: '',
    image: '',
    display_airlines: buildAirlines(airlineCodes, dict),
    segments_quantity: segments.length || 1,
    is_direct: segments.length <= 1,
    flight_time: flightTime,
    dep_at: depAt,
    arr_at: arrAt,
  };

  const mappedFlights = mapWatchlistOfferToMappedFlights(dict, item.offer);
  const flightCountryCodes = buildFlightCountryCodes(item.offer, dict);

  return {
    ...mapOffersResults(result),
    offerHash: item.hash,
    mappedFlights,
    flightCountryCodes,
  };
}
